// Shared constants and helpers for the Manual Registrations screens

export const PAYMENT_METHODS = [
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

export const CHANGE_TYPES = {
  new: 'New subscription',
  trial_conversion: 'Trial converted to paid',
  renewal: 'Renewal',
  upgrade: 'Upgrade',
  downgrade: 'Downgrade',
};

export const REGISTRATION_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Receipt rules shown in the form and checked before upload
export const PROOF_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
export const PROOF_ACCEPT = '.png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf';
export const MAX_PROOF_BYTES = 5 * 1024 * 1024;

export const methodLabel = (value) => (value === 'razorpay' ? 'Razorpay (online)'
  : PAYMENT_METHODS.find((m) => m.value === value)?.label || value || '—');

// "Paid via" in the paid history: through the application, or recorded manually by the Product Admin
export const PAID_VIA_OPTIONS = [
  { value: 'online', label: 'Online (application)' },
  ...PAYMENT_METHODS.map((m) => ({ value: m.value, label: `Manual · ${m.label}` })),
];
export const paidViaLabel = (value) => PAID_VIA_OPTIONS.find((o) => o.value === value)?.label || value || '—';

// Subscription status shown for each payment
export const HISTORY_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const changeTypeLabel = (value) => CHANGE_TYPES[value] || value || '—';

// Manual payments are recorded in rupees
export function formatINR(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `₹${number.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Returns an error message, or null when the file is acceptable
export function validateProofFile(file) {
  if (!file) return 'Upload the payment screenshot or receipt';
  const extensionOk = /\.(png|jpe?g|pdf)$/i.test(file.name || '');
  if (!PROOF_TYPES.includes(file.type) || !extensionOk) return 'Only PNG, JPG, JPEG or PDF files are allowed';
  if (file.size > MAX_PROOF_BYTES) return `The file is ${formatFileSize(file.size)}; the limit is 5 MB`;
  if (file.size === 0) return 'The file is empty';
  return null;
}

// Whole-number text the way people type it ("12,000" -> 12000); NaN when it is not a number
export function parseAmount(text) {
  const cleaned = String(text ?? '').replace(/[,\s₹]/g, '');
  return cleaned === '' ? NaN : Number(cleaned);
}


// ---- dates (local calendar days, as people enter them in <input type="date">) ----
const pad = (n) => String(n).padStart(2, '0');
// Today as the calendar day in the admin's own time zone
export const todayISO = () => toInputDate(new Date());
export const toInputDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
export const fromInputDate = (text) => (text ? new Date(`${text}T00:00:00`) : null);
export const addDays = (date, days) => new Date(date.getTime() + days * 86400000);
export const daysBetween = (from, to) => Math.round((to.getTime() - from.getTime()) / 86400000);

// How far back a subscription may start (a customer who paid some days ago)
export const MAX_BACKDATE_DAYS = 90;

// ---- pricing: the same rule as the online checkout (price per licence x licences, plus GST) ----
export const GST_RATE = 18;   // percent; the backend reads it from GST_RATE

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export function priceBreakdown(pricePerLicence, licences, gstRate = GST_RATE) {
  const taxable = round2(pricePerLicence * licences);
  const gst = round2((taxable * gstRate) / 100);
  return { taxable, gst, total: round2(taxable + gst), gstRate };
}
